import type { PermissionRegistryModule } from "@/types/api/permissions";

import { applyPickerSubgroups } from "@/components/settings/permissions/permissionPickerSubgroups";

const SETTINGS_NAV_KEY = "nav.settings";
const PROJECTS_NAV_KEY = "nav.projects";
const FINANCE_NAV_KEY = "nav.finance";

/** Matches API PERMISSION_VIEW_PAIRS — write/create/generate/delete grant paired view (or nav) keys. */
const PERMISSION_VIEW_PAIRS: Partial<Record<string, string>> = {
  "projects.create": PROJECTS_NAV_KEY,
  "projects.delete": PROJECTS_NAV_KEY,
  "project.projectServiceFront.write": "project.projectServiceFront.view",
  "project.projectServiceFront.delete": "project.projectServiceFront.view",
  "project.projectDiary.write": "project.projectDiary.view",
  "project.projectDiary.delete": "project.projectDiary.view",
  "project.materials.write": "project.materials.view",
  "project.materials.delete": "project.materials.view",
  "project.equipments.write": "project.equipments.view",
  "project.equipments.delete": "project.equipments.view",
  "project.documents.write": "project.documents.view",
  "project.documents.delete": "project.documents.view",
  "project.budgets.write": "project.budgets.view",
  "project.budgets.delete": "project.budgets.view",
  "project.budgets.approve": "project.budgets.view",
  "project.clientBudgets.write": "project.clientBudgets.view",
  "project.clientBudgets.delete": "project.clientBudgets.view",
  "project.clientBudgets.approve": "project.clientBudgets.view",
  "project.purchaseRequests.write": "project.purchaseRequests.view",
  "project.purchaseRequests.approve": "project.purchaseRequests.view",
  "project.quotations.write": "project.quotations.view",
  "project.quotations.approve": "project.quotations.view",
  "project.purchaseOrders.write": "project.purchaseOrders.view",
  "finance.payables.write": "finance.payables.view",
  "finance.payables.delete": "finance.payables.view",
  "finance.receivables.write": "finance.receivables.view",
  "finance.receivables.delete": "finance.receivables.view",
  "finance.fiscal.emit": "finance.fiscal.view",
  "finance.fiscal.cancel": "finance.fiscal.view",
  "project.photos.write": "project.photos.view",
  "project.photos.delete": "project.photos.view",
  "project.projectReport.generate": "project.projectReport.view",
  "project.projectReport.delete": "project.projectReport.view",
};

function hasSettingsModuleAccess(keys: ReadonlySet<string>): boolean {
  return [...keys].some(
    (key) =>
      key.startsWith("settings.") ||
      key === "project.projectReport.manageModels" ||
      key === "users.manage" ||
      key === "templates.manage",
  );
}

function hasFinanceModuleAccess(keys: ReadonlySet<string>): boolean {
  return [...keys].some((key) => key.startsWith("finance."));
}

function hasProjectsModuleAccess(keys: ReadonlySet<string>): boolean {
  return [...keys].some(
    (key) =>
      key === PROJECTS_NAV_KEY ||
      key === "projects.create" ||
      key === "projects.delete" ||
      (key.startsWith("project.") && key !== "project.projectReport.manageModels"),
  );
}

/** nav.projects is implied only when another obra key grants list access — not when it is the sole selection. */
function isProjectsNavImpliedByOtherKeys(keys: ReadonlySet<string>): boolean {
  return [...keys].some(
    (item) =>
      item === "projects.create" ||
      item === "projects.delete" ||
      (item.startsWith("project.") && item !== "project.projectReport.manageModels"),
  );
}

function applyViewImpliedPermissions(keys: ReadonlySet<string>): Set<string> {
  const next = new Set(keys);
  for (const key of keys) {
    const view = PERMISSION_VIEW_PAIRS[key];
    if (view) next.add(view);
  }
  return next;
}

function isViewKeyImplied(key: string, keys: ReadonlySet<string>): boolean {
  if (key === SETTINGS_NAV_KEY || key === PROJECTS_NAV_KEY) return false;
  return [...keys].some((grantKey) => PERMISSION_VIEW_PAIRS[grantKey] === key);
}

/** Auto-grant implied view + nav keys so the picker draft matches stored permissions. */
export function applyImpliedPermissions(keys: ReadonlySet<string>): Set<string> {
  const next = applyViewImpliedPermissions(keys);

  if (hasSettingsModuleAccess(next)) next.add(SETTINGS_NAV_KEY);
  else next.delete(SETTINGS_NAV_KEY);

  if (hasFinanceModuleAccess(next)) next.add(FINANCE_NAV_KEY);
  else next.delete(FINANCE_NAV_KEY);

  if (hasProjectsModuleAccess(next)) next.add(PROJECTS_NAV_KEY);
  else next.delete(PROJECTS_NAV_KEY);

  return next;
}

export function isNavKeyImplied(key: string, keys: ReadonlySet<string>): boolean {
  if (key === SETTINGS_NAV_KEY) return hasSettingsModuleAccess(keys);
  if (key === FINANCE_NAV_KEY) return hasFinanceModuleAccess(keys);
  if (key === PROJECTS_NAV_KEY) return isProjectsNavImpliedByOtherKeys(keys);
  return false;
}

export function isPickerKeyImplied(key: string, keys: ReadonlySet<string>): boolean {
  return isNavKeyImplied(key, keys) || isViewKeyImplied(key, keys);
}

export function isPermissionHiddenFromUi(key: string): boolean {
  return key === SETTINGS_NAV_KEY || key === FINANCE_NAV_KEY;
}

export const GROUP_ORDER = ["app", "finance", "settings", "projects", "project", "admin"] as const;

export type PermissionModuleSubgroup = {
  labelKey: string;
  keys: readonly string[];
};

export type PermissionModuleSection = {
  id?: string;
  labelKey: string;
  descriptionKey?: string;
  keys: readonly string[];
  groups?: readonly PermissionModuleSubgroup[];
};

export type PermissionModule = {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  keys?: readonly string[];
  sections?: readonly PermissionModuleSection[];
};

export function modulesFromRegistry(
  modules: PermissionRegistryModule[] | undefined,
): readonly PermissionModule[] {
  if (!modules?.length) return [];
  return modules.map((module) => ({
    id: module.id,
    labelKey: module.labelKey,
    descriptionKey: module.descriptionKey,
    keys: module.keys,
    sections: module.sections?.map((section) =>
      applyPickerSubgroups({
        id: section.id,
        labelKey: section.labelKey,
        descriptionKey: section.descriptionKey,
        keys: section.keys,
      }),
    ),
  }));
}

export type AccessMode = "none" | "custom";

export function moduleKeys(module: PermissionModule): string[] {
  if (module.keys) return [...module.keys];
  return module.sections?.flatMap((section) => [...section.keys]) ?? [];
}

function userFacingModuleKeys(keys: string[], normalized: ReadonlySet<string>): string[] {
  return keys.filter(
    (key) =>
      !isPermissionHiddenFromUi(key) &&
      !isNavKeyImplied(key, normalized) &&
      !isViewKeyImplied(key, normalized),
  );
}

export function resolveAccessMode(selected: ReadonlySet<string>, keys: string[]): AccessMode {
  const normalized = applyImpliedPermissions(selected);
  const userFacingKeys = userFacingModuleKeys(keys, normalized);
  const granted = userFacingKeys.filter((key) => normalized.has(key));
  return granted.length === 0 ? "none" : "custom";
}

export function labelForKey(
  key: string,
  labelByKey: Map<string, string>,
  t: (key: string) => string,
): string {
  const labelKey = labelByKey.get(key);
  return labelKey ? t(labelKey) : key;
}

export type ModuleAccessStatus =
  | { kind: "none" }
  | { kind: "full" }
  | { kind: "custom"; count: number; grantedKeys: string[] };

export function resolveModuleAccessStatus(
  selected: ReadonlySet<string>,
  module: PermissionModule,
): ModuleAccessStatus {
  const keys = moduleKeys(module);
  const normalized = applyImpliedPermissions(selected);
  const userFacingKeys = userFacingModuleKeys(keys, normalized);
  const grantedKeys = userFacingKeys.filter((key) => normalized.has(key));

  if (grantedKeys.length === 0) return { kind: "none" };
  if (grantedKeys.length === userFacingKeys.length) return { kind: "full" };
  return { kind: "custom", count: grantedKeys.length, grantedKeys };
}

export function resolveModuleStatusLabel(
  status: ModuleAccessStatus,
  t: (key: string, options?: { count: number }) => string,
): string {
  if (status.kind === "none") return t("permissions.access.none");
  if (status.kind === "full") return t("permissions.access.full");
  return t("permissions.access.custom", { count: status.count });
}
